//
// # Layout — Render Tier
//
// The DOM-free render core for a layout: the drawable entity trees plus the
// state the draw path reads. Kept in its own module (no modes/keys/interaction
// imports) so the headless server can build and redraw a layout without pulling
// in the browser interaction tier. The interaction facade `LayoutContent` (in
// `content.ts`) owns a `LayoutRender` and delegates to it.
//

// Local Imports
import { Result, Context } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import { model, layout, graphics as g } from "GenAlphaModel";
import type { RenderHost } from "../../render_host";
import * as style from "../../style";
import { ContentKind } from "../kind";
import { RenderContentBase } from "../base_render";
import { LayoutEntity } from "./layout";
import type { InstanceEntity } from "./instance";
import type { Parent } from "../../tree";
import { DrawTo, LayoutDisplayOptions } from "../../draw";

/** A labelled box drawn over a layout, in its units; for views that point at
 * several places at once. */
export interface LayoutMarker {
  bbox: { min: { x: number; y: number }; max: { x: number; y: number } };
  label?: string;
  color: style.Color;
}

/**
 * DRC error information needed for highlighting.
 * This interface matches the relevant fields from the IDE's DrcError type.
 */
export interface DrcErrorInfo {
  kind:
    | "instance_overlap"
    | "min_spacing"
    | "min_width"
    | "min_area"
    | "parameter_violation"
    | "min_enclosure"
    | "tap_coverage"
    | "tap_net";
  ruleId: string;
  severity: "error" | "warning" | "info";
  /** Instance names involved in this error */
  instances?: string[];
  /** Bounding box of the error region */
  bbox?: {
    min: { x: number; y: number };
    max: { x: number; y: number };
  };
}

class Walker {
  readonly layouts: model.IdentMap<layout.Layout>;
  constructor(layouts?: model.IdentMap<layout.Layout>) {
    this.layouts = layouts ?? new model.IdentMap<layout.Layout>();
  }

  static walk(lay: layout.Layout): model.IdentMap<layout.Layout> {
    const walker = new Walker();
    walker.layout(lay);
    return walker.layouts;
  }

  layout = (lay: layout.Layout) => {
    if (this.layouts.has(lay.ident)) return; // Already done
    this.layouts.set(lay);
    if (!lay.impl) return;
    for (const inst of lay.impl.instances) {
      const { target } = inst;
      if (target.kind === "bound") {
        this.layout(target.layout);
      } else {
        // FIXME: merge these in some more, be able to fetch
        // this.layouts.get(target.ident)?.impl?.instances.forEach(this.layout);
      }
    }
  };
}

//
// # Layout — Render Tier
//
// The drawable state of a layout: the layout/stack entity trees, the model
// `data`, PDK + layer visibility + display options, the shared-entity cache, and
// the DRC bbox highlight read by redraw. Extends `RenderContentBase` — the entity
// tree roots here, not on the `LayoutContent` facade. No selection/editing/modes;
// those live on `LayoutContent`.
//
export class LayoutRender extends RenderContentBase {
  override readonly contentKind = ContentKind.Layout;

  // Data members
  // Primary sub-drawer for the layout
  readonly layoutEntity: LayoutEntity;
  // The PDK metal stack, when the layout has one. Not drawn; consumed by
  // route drawing (via definitions) and routing-grid snapping.
  readonly stack: layout.Stack | null = null;
  // Layout definitions, including top and those used by instances
  readonly layouts: model.IdentMap<layout.Layout>;
  public schematicImpl?: model.ident.Ident; // Backward reference to the schematic this layout implements
  public pdkId?: string; // PDK identifier

  /** Layer visibility state - layers in this set are hidden from display */
  readonly hiddenLayers = new Set<number>();

  /** Display options controlling metadata overlay visibility independently from layers. */
  layoutDisplay = LayoutDisplayOptions.default();

  /**
   * Per-segment selection tracking for multi-segment (generator) instances.
   *
   * When an InstanceEntity has an entry here, only the segments whose indices
   * are in the Set are "actively selected" (blue highlight). Sibling segments
   * of the same instance get a dimmer purple highlight to show they belong to
   * the same generator but are not part of the current move/edit.
   *
   * The parent InstanceEntity still lives in `LayoutContent.selection` (for
   * compat with deleteSelected, duplicateSelected, etc.); this map adds
   * per-segment granularity on top.
   *
   * When no entry exists for an InstanceEntity (or it was selected via
   * box-select / "Select All Segments"), whole-instance semantics apply and
   * all segments get the standard blue highlight.
   *
   * Lives on the render tier because `InstanceEntity.redraw` reads it (via
   * `root`) to pick per-segment highlight colors; `LayoutContent` mutates it
   * through the selection code via a delegating getter.
   */
  readonly selectedSegments = new Map<InstanceEntity, Set<number>>();

  /**
   * Cache of shared LayoutEntity trees, keyed by the Layout model object.
   * Each unique Layout gets a single LayoutEntity tree shared across all
   * ScalarInstanceEntity objects that reference it. This dramatically reduces
   * entity count for designs with many instances of the same layouts.
   *
   * WeakMap is used so entries are garbage collected when the Layout model
   * object is no longer referenced elsewhere.
   */
  readonly layoutEntityCache = new WeakMap<layout.Layout, LayoutEntity>();

  /** Region highlight for the current DRC error's bbox (null when none). Used
   * for errors that carry a bbox but no instance, e.g. route-width violations.
   * Public so `LayoutContent`'s highlight methods can set it. */
  drcHighlightBbox: DrcErrorInfo["bbox"] | null = null;

  /** Labelled boxes over the cell, drawn after the DRC region. */
  markers: ReadonlyArray<LayoutMarker> = [];

  // A marker's label, in screen pixels above its box's top-left corner.
  private static readonly MARKER_LABEL_PX = 11;

  // Outline style for the current cell's own outline
  private static readonly OUTLINE_STYLE: style.ShapeStyle = {
    fill: style.Special.None,
    stroke: style.Color.cyan,
    linewidth: 2,
    lineDash: [100, 50],
    cap: "butt",
    join: "miter",
  };

  // Highlight box for a DRC error region (used when the error has a bbox but no
  // instance to highlight, e.g. route-width violations).
  private static readonly DRC_HIGHLIGHT_STYLE: style.ShapeStyle = {
    fill: style.Color.new(255, 60, 60, 0.15),
    stroke: style.Color.new(255, 60, 60, 1),
    linewidth: 2,
    cap: "butt",
    join: "miter",
  };

  private constructor(
    editor: RenderHost,
    readonly data: layout.Layout // Note `data` *is* stored as a member, unlike many points in this codebase's history
  ) {
    super(editor);

    const { stack, pdk } = data;
    const { bbox } = data.abs;
    this.bbox = bbox; // This is a parent class field; unclear whether we still need it.

    // Set PDK from data (parallel to how other readonly fields are set)
    this.pdk = pdk ?? null;

    // Create our primary sub-drawer
    this.layoutEntity = new LayoutEntity({ data, parent: this }, 0);
    if (stack) {
      this.stack = stack;
      // Set grid snap from PDK stack base for layout-specific snapping
      const [snapX, snapY] = stack.base;
      editor.grid.updateSettings({ snapX, snapY });
    }

    // Set rendering context on the canvas so DrawToHtmlCanvas can propagate
    // pdk and layer visibility to entity redraw methods without walking the
    // entity parent chain. hiddenLayers is a mutable Set, so passing the
    // reference once keeps the canvas in sync with future visibility toggles.
    this.editor.canvas.pdk = this.pdk;
    this.editor.canvas.hiddenLayers = this.hiddenLayers;

    // And get a map of all our available layouts
    this.layouts = Walker.walk(data);
  }

  static new(editor: RenderHost, data: layout.Layout): LayoutRender {
    return new LayoutRender(editor, data);
  }

  // Load a layout render tier from schema (server render path): build the model
  // hierarchy off the depot, construct the render tier, copy the backward
  // schematic ref + PDK id, and backfill outlines.
  static async fromSchema(
    host: RenderHost,
    data: schema.Layout
  ): Promise<Result<LayoutRender>> {
    const make = await layout.Layout.fromSchema(
      Context.background(),
      data,
      host.depot,
      "" // root namespace
    );
    return make.andThen((lay) => {
      const render = LayoutRender.new(host, lay);
      if (data.schematicImpl) render.schematicImpl = data.schematicImpl;
      if (lay.pdkId) render.pdkId = lay.pdkId;
      render.initOutline();
      return Result.Ok(render);
    });
  }

  // Backfill outlines from bbox when unset (idempotent once persisted). Same
  // logic the interaction facade runs on load; the render tier needs it so the
  // server path draws cell outlines. Uses only render-tier state + the DOM-free
  // `platform.notifyUIChange` (a no-op on the headless host).
  initOutline = (): void => {
    if (!this.data.abs.outline) {
      const outline = model.Bbox.clone(this.data.abs.bbox);
      this.data.abs.outline = outline;
      this.editor.platform.notifyUIChange(
        model.LayoutChange.setOutline(outline)
      );
    }
    for (const lay of this.layouts.values()) {
      lay.abs.outline ??= model.Bbox.clone(lay.abs.bbox);
    }
  };

  // Under composition the render object is the entity-tree root.
  override get root(): this {
    return this;
  }

  /**
   * Get or create a shared LayoutEntity for the given layout.
   * On cache hit, returns the shared entity. On cache miss, creates a new
   * LayoutEntity, caches it, and returns it.
   *
   * The first caller's `parent` becomes the entity's structural parent.
   * This is acceptable because `this.root` walks to the same LayoutRender
   * regardless of which instance's parent chain it traverses.
   *
   * @param lay - The Layout model object to get/create an entity for
   * @param hierLevel - The hierarchy level for ImplEntity creation
   * @param parent - The parent entity (used only on first creation)
   */
  getOrCreateLayoutEntity(
    lay: layout.Layout,
    hierLevel: number,
    parent: Parent<LayoutRender>
  ): LayoutEntity {
    const existing = this.layoutEntityCache.get(lay);
    if (existing) return existing;

    const entity = new LayoutEntity({ data: lay, parent }, hierLevel);
    this.layoutEntityCache.set(lay, entity);
    return entity;
  }

  override redraw = (to: DrawTo): unknown => {
    to.layer((to) => this.layoutEntity.redraw(to));
    // Draw top-level routes and path elements as overlays (outside sprites) so
    // they remain visible at all zoom levels.
    //
    // Wrap in `to.overlay("routes", ...)` so these primitives are attributed
    // to the overlay.routes bucket rather than the direct bucket, matching
    // the instance-level path in `ScalarInstanceEntity.redraw`.
    //
    // Pass `to.viewport` as the localViewport. At the root, the current cell
    // is drawn in world coordinates (no instance xform), so the "local"
    // viewport IS `to.viewport` directly - no inverse-xform needed. This
    // enables per-route and per-path viewport culling at the top level,
    // matching what the instance path already gets.
    const { implEntity } = this.layoutEntity;
    if (implEntity) {
      to.overlay("routes", (to) => {
        // Top-level route visibility - when off, skip the route overlay entirely.
        // Path elements are unrelated to the "Routes" parent toggle, so they
        // still draw. (`pathElements` here = generic cell-level paths, not routes.)
        if (this.layoutDisplay.routes) {
          implEntity.routes.redraw(to, to.viewport);
        }
        implEntity.redrawPathElements(to, to.viewport);
      });
    }
    // Draw the current cell's own outline when outlines are enabled and outline is set
    if (this.editor.showOutlines && this.data.abs.outline) {
      to.bbox(this.data.abs.outline, {
        ...LayoutRender.OUTLINE_STYLE,
        linewidth: LayoutRender.OUTLINE_STYLE.linewidth / to.scale,
      });
    }
    // DRC error region highlight (for errors with a bbox but no instance).
    if (this.drcHighlightBbox) {
      to.bbox(this.drcHighlightBbox, {
        ...LayoutRender.DRC_HIGHLIGHT_STYLE,
        linewidth: LayoutRender.DRC_HIGHLIGHT_STYLE.linewidth / to.scale,
      });
    }
    for (const m of this.markers) this.drawMarker(to, m);
    // Mode-specific overlays (ruler, box-select, etc.) are drawn by the
    // generic mode draw hook in editor.redraw().
    return null;
  };

  private drawMarker(to: DrawTo, m: LayoutMarker): void {
    const { r, g: green, b } = m.color;
    to.bbox(m.bbox, {
      fill: style.Color.new(r, green, b, 0.15),
      stroke: style.Color.new(r, green, b, 1),
      linewidth: 2 / to.scale,
      cap: "butt",
      join: "miter",
    });
    if (m.label === undefined || m.label === "") return;
    const size = LayoutRender.MARKER_LABEL_PX / to.scale;
    to.text(
      g.Text.fromPartial({
        text: m.label,
        loc: { x: m.bbox.min.x, y: m.bbox.max.y + size / 4 },
        height: size,
        alignment: g.Alignment.Left,
        baseline: g.Baseline.Bottom,
        rotation: 0,
        hidden: false,
      }),
      {
        family: style.THE_FONT,
        style: "normal",
        weight: 700,
        size,
        fill: style.Color.new(r, green, b, 1),
        stroke: style.Special.None,
      },
      { xform: model.Xform.default() }
    );
  }

  // Recompute the bounding box of all content from the current entity state.
  // Called before fit-to-view to ensure newly added entities are included.
  // Only walks one depth - sub-cell bboxes are already recursive in lay.abs.bbox.
  override recalcBbox = (): void => {
    const bboxes: Array<model.Bbox> = [];

    const { implEntity } = this.layoutEntity;
    if (implEntity) {
      for (const instance of implEntity.instances.values()) {
        instance.collectBboxes(bboxes);
      }
    }

    // Include pins (which live on the abstract layer, not the impl)
    for (const pin of this.layoutEntity.absEntity.pins.values()) {
      bboxes.push(pin.bbox());
    }

    // A leaf cell (a foundry device, say) is mostly its own shapes.
    for (const elem of this.data.impl?.elems ?? []) {
      bboxes.push(g.LayeredElement.bbox(elem));
    }

    // Fall back to the stored (recursively-computed) cell bbox when the live
    // scan finds nothing to measure — e.g. a cell with only top-level routes.
    // `data.abs.bbox` already covers those, so fit-to-view doesn't collapse to
    // an empty region. (Live routes are not re-measured.)
    this.bbox =
      bboxes.length > 0 ? model.Bbox.getBboxUnion(bboxes) : this.data.abs.bbox;
  };
}
